import { useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CheckCircle, Warning, ArrowLeft, CircleNotch, PencilSimple, Sparkle, FilePdf, DownloadSimple, Play, ShieldCheck, Robot, Question, Prohibit } from "@phosphor-icons/react";
import ReadBackPanel from "../components/portal/ReadBackPanel";
import TBAutoMapPanel from "../components/portal/TBAutoMapPanel";

const STATE_STYLES = {
  auto_accepted: "bg-emerald-50 text-emerald-800 border-emerald-200",
  preparer_confirmed: "bg-emerald-100 text-emerald-900 border-emerald-300",
  preparer_overridden: "bg-indigo-50 text-indigo-800 border-indigo-200",
  needs_review: "bg-amber-50 text-amber-800 border-amber-200",
  extracted: "bg-zinc-100 text-zinc-700 border-zinc-200",
  written_to_drake: "bg-slate-900 text-white border-slate-900",
};

const STATE_LABEL = {
  auto_accepted: "Auto-accepted",
  preparer_confirmed: "Confirmed",
  preparer_overridden: "Overridden",
  needs_review: "Needs review",
  extracted: "Extracted",
  written_to_drake: "Written to Drake",
};

function money(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatEIN(v) {
  if (!v) return "—";
  const s = String(v).replace(/\D/g, "");
  return s.length === 9 ? `${s.slice(0, 2)}-${s.slice(2)}` : v;
}

function displayValue(f) {
  if (f.value_type === "currency") return `$${money(f.value)}`;
  if (f.value_type === "ein") return formatEIN(f.value);
  if (f.value_type === "boolean") return f.value ? "Yes" : (f.value === false ? "No" : "—");
  return f.value ?? "—";
}

function FieldRow({ f, onConfirm, onOverride, onFocus, focused, onAskClient }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(f.value ?? "");
  const pill = STATE_STYLES[f.state] || STATE_STYLES.extracted;
  const confPct = Math.round((f.confidence || 0) * 100);
  return (
    <div
      data-testid={`field-row-${f.id}`}
      onClick={() => onFocus(f)}
      className={`grid grid-cols-12 gap-2 items-center border-b border-zinc-100 px-3 py-2 text-xs cursor-pointer transition-colors duration-100 ${focused ? "bg-emerald-50/30" : "hover:bg-zinc-50"}`}>
      <div className="col-span-4 min-w-0">
        <p className="text-slate-900 font-medium truncate">{f.field_label}</p>
        <p className="text-[9px] font-mono text-zinc-400 truncate">{f.mef_path}</p>
      </div>
      <div className="col-span-3 min-w-0">
        {editing ? (
          <Input
            data-testid={`field-edit-${f.id}`}
            value={draft}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { onOverride(f, draft); setEditing(false); }
              if (e.key === "Escape") { setDraft(f.value ?? ""); setEditing(false); }
            }}
            className="h-6 text-xs font-mono"
          />
        ) : (
          <p className={`font-mono truncate ${f.state === "preparer_overridden" ? "text-indigo-700" : "text-slate-800"}`}>{displayValue(f)}</p>
        )}
        {f.prior_year_value !== null && f.prior_year_value !== undefined && (
          <p className="text-[9px] text-zinc-400 truncate flex items-center gap-1" data-testid={`variance-${f.id}`}>
            <span className="truncate">Prior: <span className="font-mono">{f.value_type === "currency" ? "$" + money(f.prior_year_value) : f.prior_year_value}</span></span>
            {f.variance_flag ? (
              <span
                className={`px-1 py-0.5 rounded-sm font-mono text-[8.5px] ${
                  f.variance_flag > 0.25 ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"
                }`}
                title={f.variance_flag > 0.25 ? "Prior-year variance exceeds §25% — flagged for discussion" : "Prior-year variance"}
              >
                {f.variance_flag > 0 ? "▲" : "▼"} {Math.round(f.variance_flag * 100)}%
              </span>
            ) : null}
            {f.variance_flag > 0.25 && !f.variance_note_id && onAskClient && (
              <button
                data-testid={`ask-client-btn-${f.id}`}
                title="Draft & send a portal message asking the client to explain this change"
                onClick={(e) => { e.stopPropagation(); onAskClient(f); }}
                className="text-[9px] font-medium text-indigo-700 hover:text-indigo-900 hover:underline"
              >
                Ask client →
              </button>
            )}
            {f.variance_note_id && (
              <span className="text-[9px] font-medium text-emerald-700" title="Variance note already sent to client" data-testid={`variance-sent-${f.id}`}>
                ✓ asked
              </span>
            )}
          </p>
        )}
      </div>
      <div className="col-span-2">
        <div className="flex items-center gap-1">
          <div className="flex-1 h-1 bg-zinc-100 rounded-full overflow-hidden">
            <div className={`h-full ${confPct >= 98 ? "bg-emerald-500" : confPct >= 90 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${confPct}%` }} />
          </div>
          <span className="text-[9px] font-mono text-zinc-500 tabular-nums w-8 text-right">{confPct}%</span>
        </div>
      </div>
      <div className="col-span-1.5">
        <span className={`text-[9px] uppercase tracking-wider rounded-sm border px-1.5 py-0.5 ${pill}`} data-testid={`field-state-${f.id}`}>{STATE_LABEL[f.state] || f.state}</span>
      </div>
      <div className="col-span-1.5 flex items-center justify-end gap-1">
        {f.state !== "written_to_drake" && (
          <>
            <button
              data-testid={`confirm-btn-${f.id}`}
              title="Confirm (K)"
              onClick={(e) => { e.stopPropagation(); onConfirm(f); }}
              className="p-1 rounded hover:bg-emerald-100 text-emerald-700 transition-colors duration-100">
              <CheckCircle size={13} weight="fill" />
            </button>
            <button
              data-testid={`edit-btn-${f.id}`}
              title="Override (E)"
              onClick={(e) => { e.stopPropagation(); setDraft(f.value ?? ""); setEditing(true); }}
              className="p-1 rounded hover:bg-indigo-100 text-indigo-700 transition-colors duration-100">
              <PencilSimple size={13} />
            </button>
          </>
        )}
      </div>
      {f.diagnostic && (
        <div className="col-span-12 mt-1 -mb-1 rounded-sm bg-amber-50 border border-amber-200 px-2 py-1 flex items-start gap-1.5" data-testid={`diagnostic-${f.id}`}>
          <Warning size={11} weight="fill" className="text-amber-600 mt-0.5" />
          <p className="text-[10px] text-amber-800">{f.diagnostic.message}</p>
        </div>
      )}
    </div>
  );
}

export default function TaxDataRoom() {
  const { engagementId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [focusedField, setFocusedField] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["drake-fields", engagementId],
    queryFn: () => api.get(`/drake/fields?engagement_id=${engagementId}`).then((r) => r.data),
  });
  const { data: engagement } = useQuery({
    queryKey: ["engagement", engagementId],
    queryFn: () => api.get(`/engagements/${engagementId}`).then((r) => r.data),
  });
  const { data: documents } = useQuery({
    queryKey: ["engagement-docs", engagementId],
    queryFn: () => api.get(`/documents?engagement_id=${engagementId}`).then((r) => r.data),
  });
  const { data: packets } = useQuery({
    queryKey: ["drake-packets", engagementId],
    queryFn: () => api.get(`/drake/packets?engagement_id=${engagementId}`).then((r) => r.data),
  });

  const extract = useMutation({
    mutationFn: (documentId) => api.post("/drake/extract", { document_id: documentId }),
    onSuccess: (r) => {
      toast.success(`Extracted ${r.data.fields_written} fields from ${r.data.form_type}`);
      qc.invalidateQueries({ queryKey: ["drake-fields", engagementId] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  const runRules = useMutation({
    mutationFn: () => api.post(`/drake/rules/run?engagement_id=${engagementId}`),
    onSuccess: (r) => { toast.success(`${r.data.count} diagnostics found`); qc.invalidateQueries({ queryKey: ["drake-fields", engagementId] }); },
    onError: (e) => toast.error(errText(e)),
  });

  const exportPacket = useMutation({
    mutationFn: () => api.post(`/drake/packet/export?engagement_id=${engagementId}`),
    onSuccess: (r) => {
      const d = r.data;
      toast.success(d.reused ? "No new changes — reused prior packet" : `Packet ${d.packet_id?.slice(0, 8)} generated`);
      qc.invalidateQueries({ queryKey: ["drake-packets", engagementId] });
      window.open(api.defaults.baseURL + d.download_url, "_blank");
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail;
      if (detail?.blocker_count) {
        toast.error(`Cannot export — ${detail.blocker_count} fields still need review`);
      } else {
        toast.error(errText(e));
      }
    },
  });

  const confirmField = useMutation({
    mutationFn: (f) => api.patch(`/drake/fields/${f.id}`, { action: "confirm" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["drake-fields", engagementId] }),
    onError: (e) => toast.error(errText(e)),
  });
  const overrideField = useMutation({
    mutationFn: ({ f, val }) => api.patch(`/drake/fields/${f.id}`, { action: "override", value: val === "" ? null : (f.value_type === "currency" ? parseFloat(val) : val) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["drake-fields", engagementId] }),
    onError: (e) => toast.error(errText(e)),
  });

  const bulkConfirm = useMutation({
    mutationFn: ({ form_type, form_index }) => api.post(`/drake/fields/bulk-confirm`, { engagement_id: engagementId, form_type, form_index }),
    onSuccess: (r) => {
      const { confirmed, skipped_tins, scope } = r.data;
      toast.success(`${confirmed} field${confirmed === 1 ? "" : "s"} confirmed on ${scope}${skipped_tins ? ` (${skipped_tins} TIN still need review)` : ""}`);
      qc.invalidateQueries({ queryKey: ["drake-fields", engagementId] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  const askClient = useMutation({
    mutationFn: (f) => api.post(`/drake/variance-note/${f.id}`),
    onSuccess: (r) => {
      toast.success("Variance note sent to client");
      qc.invalidateQueries({ queryKey: ["drake-fields", engagementId] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  // Exception-first ordering: needs_review first
  const forms = data?.forms || [];
  const summary = data?.summary || {};
  const exceptionQueue = useMemo(() => {
    const all = [];
    for (const form of forms) {
      for (const f of form.fields) {
        if (f.state === "needs_review") all.push(f);
      }
    }
    return all;
  }, [forms]);

  const totalFields = Object.values(summary).reduce((a, b) => a + b, 0);
  const readyPct = totalFields > 0 ? Math.round(((summary.auto_accepted || 0) + (summary.preparer_confirmed || 0) + (summary.preparer_overridden || 0)) / totalFields * 100) : 0;

  const focusedDoc = focusedField ? (documents || []).find((d) => d.id === focusedField.source?.document_id) : null;
  const API = api.defaults.baseURL;

  return (
    <div className="min-h-screen bg-zinc-50 text-slate-900" data-testid="tax-data-room">
      <header className="bg-white border-b border-zinc-200 px-6 py-3 flex items-center justify-between sticky top-0 z-30 shadow-sm">
        <div className="flex items-center gap-3">
          <button data-testid="tdr-back-btn" onClick={() => navigate(-1)} className="text-zinc-500 hover:text-slate-900 transition-colors duration-150"><ArrowLeft size={16} /></button>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1"><Robot size={11} weight="fill" /> AI-Assisted Data Integration Layer · CRM → Drake</p>
            <h1 className="font-heading text-lg text-slate-900" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
              Tax Data Room · {engagement?.client_name || "Loading…"}
            </h1>
            <p className="text-[10px] text-zinc-500">Form {engagement?.service_type} · TY {engagement?.tax_year}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-2 text-[10px] text-zinc-500 mr-2" data-testid="tdr-progress">
            <div className="w-32 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${readyPct}%` }} />
            </div>
            <span className="font-mono">{readyPct}% ready</span>
          </div>
          <Button data-testid="run-rules-btn" size="sm" variant="outline" disabled={runRules.isPending} onClick={() => runRules.mutate()}>
            {runRules.isPending ? <CircleNotch size={13} className="animate-spin mr-1" /> : <Play size={13} className="mr-1" />} Run rules
          </Button>
          <Button data-testid="export-packet-btn" size="sm" disabled={exportPacket.isPending || totalFields === 0} onClick={() => exportPacket.mutate()}>
            {exportPacket.isPending ? <CircleNotch size={13} className="animate-spin mr-1" /> : <FilePdf size={13} className="mr-1" />} Export GruntWorx packet
          </Button>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Documents + extract triggers */}
        <div className="lg:col-span-3 space-y-4">
          <div className="rounded-lg border border-zinc-200 bg-white p-4 hover:shadow-sm transition-shadow duration-200" data-testid="documents-panel">
            <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5"><Sparkle size={13} weight="fill" className="text-emerald-600" /> Source documents</h3>
            {!(documents || []).length && <p className="text-xs text-zinc-400 italic">No documents uploaded yet.</p>}
            <div className="space-y-1">
              {(documents || []).map((d) => (
                <div key={d.id} data-testid={`doc-${d.id}`} className={`rounded-md border px-2 py-1.5 text-xs cursor-pointer transition-colors duration-100 ${focusedDoc?.id === d.id ? "border-slate-900 bg-slate-50" : "border-zinc-200 hover:border-slate-900"}`}
                  onClick={() => setFocusedField({ source: { document_id: d.id }, id: `focus-doc-${d.id}` })}>
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-mono text-zinc-700 truncate">{d.filename}</span>
                    <button data-testid={`extract-btn-${d.id}`} title="Run extraction" onClick={(e) => { e.stopPropagation(); extract.mutate(d.id); }} className="text-emerald-700 hover:bg-emerald-100 rounded p-0.5">
                      {extract.isPending && extract.variables === d.id ? <CircleNotch size={11} className="animate-spin" /> : <Robot size={11} />}
                    </button>
                  </div>
                  <p className="text-[9px] text-zinc-400">{d.doc_type || "unclassified"}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-zinc-200 bg-white p-4 hover:shadow-sm transition-shadow duration-200" data-testid="summary-panel">
            <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5"><ShieldCheck size={13} className="text-emerald-600" /> Autofill summary</h3>
            <div className="space-y-1 text-xs">
              {[["auto_accepted","Auto-accepted",summary.auto_accepted||0,"text-emerald-700"],
                ["preparer_confirmed","Preparer-confirmed",summary.preparer_confirmed||0,"text-emerald-800"],
                ["preparer_overridden","Overridden",summary.preparer_overridden||0,"text-indigo-700"],
                ["needs_review","Needs review",summary.needs_review||0,"text-amber-700"],
                ["written_to_drake","Written to Drake",summary.written_to_drake||0,"text-slate-900"]].map(([k,label,n,color]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-zinc-600">{label}</span>
                  <span className={`font-mono font-semibold ${color}`}>{n}</span>
                </div>
              ))}
              <div className="pt-2 mt-2 border-t border-zinc-100 flex items-center justify-between">
                <span className="text-slate-900 font-medium">Total fields</span>
                <span className="font-mono font-semibold text-slate-900">{totalFields}</span>
              </div>
            </div>
          </div>

          {(packets || []).length > 0 && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 hover:shadow-sm transition-shadow duration-200" data-testid="packets-panel">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Prior packets</h3>
              <div className="space-y-1">
                {(packets || []).map((p) => (
                  <a key={p.id} href={`${API}/api/drake/packet/${p.id}/download`} target="_blank" rel="noreferrer" data-testid={`packet-${p.id}`}
                    className="flex items-center justify-between text-[11px] rounded-md border border-zinc-200 px-2 py-1.5 hover:bg-zinc-50 hover:border-slate-900 transition-colors duration-150">
                    <span className="font-mono text-slate-800">{p.id.slice(0, 8)}</span>
                    <span className="text-zinc-500 flex items-center gap-1"><DownloadSimple size={11} /> {new Date(p.generated_at).toLocaleDateString()}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          <ReadBackPanel engagementId={engagementId} packets={packets || []} />
          <TBAutoMapPanel engagementId={engagementId} clientId={engagement?.client_id} />
        </div>

        {/* Center: Canonical fields grouped by form, exception-first */}
        <div className="lg:col-span-6 space-y-4">
          {exceptionQueue.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50/70 overflow-hidden" data-testid="exception-queue">
              <div className="px-3 py-2 border-b border-amber-200 flex items-center gap-1.5">
                <Warning size={13} weight="fill" className="text-amber-700" />
                <p className="text-xs font-semibold text-amber-900">Exception queue · {exceptionQueue.length} fields need your review</p>
              </div>
              <div className="bg-white">
                {exceptionQueue.map((f) => (
                  <FieldRow key={f.id} f={f} focused={focusedField?.id === f.id}
                    onFocus={setFocusedField}
                    onConfirm={(x) => confirmField.mutate(x)}
                    onOverride={(x, v) => overrideField.mutate({ f: x, val: v })}
                    onAskClient={(x) => askClient.mutate(x)}
                  />
                ))}
              </div>
            </div>
          )}

          {isLoading && (
            <div className="rounded-lg border border-zinc-200 bg-white px-4 py-8 text-center">
              <CircleNotch size={20} className="animate-spin mx-auto text-zinc-400 mb-1" />
              <p className="text-xs text-zinc-500">Loading canonical fields…</p>
            </div>
          )}

          {!isLoading && forms.length === 0 && (
            <div className="rounded-lg border border-dashed border-zinc-300 bg-white px-4 py-10 text-center" data-testid="empty-state">
              <Robot size={28} className="text-zinc-400 mx-auto mb-2" />
              <p className="text-sm text-slate-800 font-medium">No fields extracted yet</p>
              <p className="text-[11px] text-zinc-500 mt-1">Upload W-2s, 1099s, 1098s to Documents, then click the AI robot next to each doc to extract.</p>
            </div>
          )}

          {forms.map((form) => {
            const reviewableCount = form.fields.filter((f) => ["auto_accepted", "needs_review", "extracted"].includes(f.state)).length;
            return (
            <div key={`${form.form_type}-${form.form_index}`} className="rounded-lg border border-zinc-200 bg-white overflow-hidden hover:shadow-sm transition-shadow duration-200" data-testid={`form-${form.form_type}-${form.form_index}`}>
              <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-900">Form {form.form_type} <span className="text-zinc-400 font-normal">#{form.form_index + 1}</span></p>
                  <p className="text-[9px] font-mono text-zinc-400">{form.fields[0]?.mef_path?.replace(/\[\d+\]\/[A-Za-z]+$/, "")}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] text-zinc-500 font-mono">{form.fields.length} fields</span>
                  {reviewableCount > 0 && (
                    <button
                      data-testid={`bulk-confirm-btn-${form.form_type}-${form.form_index}`}
                      disabled={bulkConfirm.isPending}
                      onClick={() => bulkConfirm.mutate({ form_type: form.form_type, form_index: form.form_index })}
                      className="inline-flex items-center gap-1 text-[10px] font-medium rounded-md border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 px-2 py-0.5 transition-colors duration-150 disabled:opacity-50"
                      title="Confirm every reviewable field on this form in one click (TINs still need per-field review)"
                    >
                      <CheckCircle size={10} weight="fill" /> Confirm all ({reviewableCount})
                    </button>
                  )}
                </div>
              </div>
              <div>
                {form.fields.map((f) => (
                  <FieldRow key={f.id} f={f} focused={focusedField?.id === f.id}
                    onFocus={setFocusedField}
                    onConfirm={(x) => confirmField.mutate(x)}
                    onOverride={(x, v) => overrideField.mutate({ f: x, val: v })}
                    onAskClient={(x) => askClient.mutate(x)}
                  />
                ))}
              </div>
            </div>
          );})}
        </div>

        {/* Right: Source doc preview + provenance */}
        <div className="lg:col-span-3">
          <div className="rounded-lg border border-zinc-200 bg-white p-4 sticky top-[80px]" data-testid="source-panel">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Source document</h3>
            {focusedDoc ? (
              <div>
                <p className="font-mono text-xs text-zinc-700 truncate mb-1">{focusedDoc.filename}</p>
                <p className="text-[10px] text-zinc-500 mb-2">{focusedDoc.doc_type}</p>
                {focusedDoc.mime_type?.startsWith("image/") ? (
                  <img src={`${API}/api/documents/${focusedDoc.id}/file`} alt="source" className="w-full rounded-md border border-zinc-200" data-testid="source-preview-img" onError={(e) => (e.currentTarget.style.display = "none")} />
                ) : (
                  <a href={`${API}/api/documents/${focusedDoc.id}/file`} target="_blank" rel="noreferrer" className="block text-center rounded-md border border-zinc-200 py-6 text-xs text-slate-700 hover:bg-zinc-50">
                    Open source document ↗
                  </a>
                )}
                {focusedField?.source?.page && (
                  <p className="text-[9px] text-zinc-400 mt-2 font-mono">Page {focusedField.source.page}</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-zinc-400 italic">Click a field on the left to preview its source document.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
